import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useConvexAuth, useMutation } from "convex/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import type { Id } from "#/../convex/_generated/dataModel";
import { BetaBanner } from "#/components/BetaBanner";
import { ListingCard } from "#/components/listing-card";
import { SupportBanner } from "#/components/SupportBanner";
import { Button } from "#/components/ui/button";
import { useMounted } from "#/hooks/useMounted";
import { useSignInModal } from "#/hooks/useSignInModal";

export const Route = createFileRoute("/")({
	head: () => ({
		meta: [
			{ title: "Re:Disc — Buy & Sell Disc Golf Discs" },
			{
				name: "description",
				content:
					"Browse and sell used disc golf discs. Connect directly with players — free to list, no middleman, no commission.",
			},
			{ property: "og:title", content: "Re:Disc — Disc Golf Marketplace" },
			{
				property: "og:description",
				content:
					"Browse and sell used disc golf discs. Free to list — connect directly with players in your community.",
			},
		],
	}),
	component: Home,
});

const BROWSE_SEARCH = {
	sort: "newest" as const,
	q: "",
	discClass: [] as string[],
	condition: [] as string[],
	color: [] as string[],
	shipsTo: [] as string[],
	country: [] as string[],
	plasticType: [] as string[],
	priceMin: "" as number | "",
	priceMax: "" as number | "",
	weightMin: "" as number | "",
	weightMax: "" as number | "",
	discModelIds: [] as string[],
	manufacturerIds: [] as string[],
	speedMin: "" as number | "",
	speedMax: "" as number | "",
	glideMin: "" as number | "",
	glideMax: "" as number | "",
	turnMin: "" as number | "",
	turnMax: "" as number | "",
	fadeMin: "" as number | "",
	fadeMax: "" as number | "",
	distanceKm: "" as number | "",
};

function StatCard({
	value,
	label,
	sub,
	accent = false,
}: {
	value: string;
	label: string;
	sub: string;
	accent?: boolean;
}) {
	return (
		<div className="relative z-10 flex min-w-[230px] items-center gap-5 rounded-[var(--radius)] border border-border bg-card px-5 py-4 shadow-sm transition-all duration-200 hover:translate-x-1 hover:shadow-lg">
			<span
				className={`display-title text-5xl leading-none ${accent ? "text-accent-accessible" : "text-primary-accessible"}`}
			>
				{value}
			</span>
			<div>
				<div className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
					{label}
				</div>
				<div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>
			</div>
		</div>
	);
}

function StatCardSkeleton() {
	return (
		<div className="relative z-10 flex min-w-[230px] items-center gap-5 rounded-[var(--radius)] border border-border bg-card px-5 py-4 shadow-sm">
			<div className="h-12 w-12 shrink-0 animate-pulse rounded-md bg-muted" />
			<div className="flex flex-col gap-1.5">
				<div className="h-2 w-20 animate-pulse rounded-full bg-muted" />
				<div className="h-3 w-28 animate-pulse rounded-full bg-muted" />
			</div>
		</div>
	);
}

const DISC_TYPES = [
	{
		labelKey: "discTypes.distanceDrivers" as const,
		discClass: "distance-driver",
		color: "#e05500",
		gradId: "dd",
	},
	{
		labelKey: "discTypes.fairwayDrivers" as const,
		discClass: "fairway-driver",
		color: "#1a66cc",
		gradId: "fd",
	},
	{
		labelKey: "discTypes.midranges" as const,
		discClass: "midrange",
		color: "#22a060",
		gradId: "mr",
	},
	{
		labelKey: "discTypes.putters" as const,
		discClass: "putter",
		color: "#9333ea",
		gradId: "pt",
	},
] as const;

function TypeDiscSvg({ color, gradId }: { color: string; gradId: string }) {
	return (
		<svg viewBox="0 0 100 100" width="76" height="76" aria-hidden="true">
			<circle
				cx="50"
				cy="50"
				r="46"
				fill="none"
				stroke={color}
				strokeWidth="7"
			/>
			<circle
				cx="50"
				cy="50"
				r="30"
				fill="none"
				stroke={color}
				strokeWidth="1.5"
				opacity=".4"
			/>
			<circle cx="50" cy="50" r="18" fill={color} opacity=".09" />
			<circle cx="50" cy="50" r="7" fill={color} opacity=".3" />
			<defs>
				<radialGradient id={`tg-${gradId}`} cx="35%" cy="35%">
					<stop offset="0%" stopColor="white" stopOpacity=".35" />
					<stop offset="100%" stopColor="white" stopOpacity="0" />
				</radialGradient>
			</defs>
			<circle cx="50" cy="50" r="46" fill={`url(#tg-${gradId})`} />
		</svg>
	);
}

function Home() {
	const { t } = useTranslation("home");
	const mounted = useMounted();
	const { isAuthenticated } = useConvexAuth();
	const { open: openSignIn } = useSignInModal();
	const { data: listings = [], isPending: listingsPending } = useQuery(
		convexQuery(api.listings.listFeatured, {}),
	);
	const { data: stats } = useQuery(convexQuery(api.listings.getStats, {}));
	const { data: watchlistItems = [] } = useQuery(
		convexQuery(api.watchlist.getWatchlist, {}),
	);
	const savedIds = useMemo(
		() => new Set(watchlistItems.map((item) => item._id as string)),
		[watchlistItems],
	);
	const saveToWatchlist = useMutation(api.watchlist.saveToWatchlist);
	const removeFromWatchlist = useMutation(api.watchlist.removeFromWatchlist);

	if (!mounted) return <HomeSkeleton />;

	const steps = [
		{
			num: "01",
			n: t("howItWorks.step1.n"),
			title: t("howItWorks.step1.title"),
			body: t("howItWorks.step1.body"),
		},
		{
			num: "02",
			n: t("howItWorks.step2.n"),
			title: t("howItWorks.step2.title"),
			body: t("howItWorks.step2.body"),
		},
		{
			num: "03",
			n: t("howItWorks.step3.n"),
			title: t("howItWorks.step3.title"),
			body: t("howItWorks.step3.body"),
		},
	];

	function handleSaveToggle(listingId: Id<"listings">) {
		if (!isAuthenticated) {
			openSignIn();
			return;
		}
		if (savedIds.has(listingId)) {
			removeFromWatchlist({ listingId });
		} else {
			saveToWatchlist({ listingId });
		}
	}

	return (
		<>
			<BetaBanner />

			{/* ── Hero ── */}
			<section className="relative overflow-hidden bg-background">
				{/* Dot grid */}
				<div
					className="pointer-events-none absolute inset-0"
					style={{
						backgroundImage:
							"radial-gradient(circle, var(--border) 1.2px, transparent 1.2px)",
						backgroundSize: "30px 30px",
					}}
				/>
				{/* Warm diagonal wash */}
				<div
					className="pointer-events-none absolute -right-16 -top-16 h-[900px] w-[600px] rounded-[80px] bg-primary opacity-[0.05]"
					style={{ transform: "rotate(-14deg)" }}
				/>
				{/* Spinning disc — large section-level decoration */}
				<img
					src="/redisc_orange_transparent.svg"
					className="pointer-events-none absolute -right-48 top-1/2 h-[820px] w-[820px] -translate-y-1/2 opacity-[0.13]"
					aria-hidden="true"
					alt=""
				/>

				<div className="page-wrap relative z-10 grid grid-cols-1 items-center gap-12 py-24 lg:grid-cols-2 lg:py-28">
					{/* Left: copy */}
					<div>
						<p className="sec-tag rise-in" style={{ animationDelay: "0ms" }}>
							{t("hero.tag")}
						</p>
						<h1
							className="rise-in display-title text-[clamp(64px,9vw,116px)] leading-[0.88] tracking-[0.015em] text-foreground"
							style={{ animationDelay: "80ms" }}
						>
							{t("hero.heading_1")}
							<br />
							{t("hero.heading_2")}
							<br />
							<span className="text-primary">{t("hero.heading_3")}</span>
							<br />
							{t("hero.heading_4")}
						</h1>
						<p
							className="rise-in mt-6 max-w-[430px] text-[17px] leading-[1.7] text-muted-foreground"
							style={{ animationDelay: "160ms" }}
						>
							{t("hero.body")}
						</p>
						<div
							className="rise-in mt-9 flex flex-wrap gap-3"
							style={{ animationDelay: "240ms" }}
						>
							<Button size="lg" asChild>
								<Link to="/listings" search={BROWSE_SEARCH}>
									{t("hero.cta_browse")}
								</Link>
							</Button>
							<Button size="lg" variant="outline" asChild>
								<Link to="/sell">{t("hero.cta_sell")}</Link>
							</Button>
						</div>
						<p
							className="rise-in mt-4 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"
							style={{ animationDelay: "320ms" }}
						>
							<span className="font-bold text-accent">✓</span>
							{t("hero.free_note")}
						</p>
					</div>

					{/* Right: stat cards — skeleton while loading, hidden when no listings */}
					<div className="relative hidden flex-col items-start gap-3 lg:flex">
						{stats === undefined ? (
							<>
								<StatCardSkeleton />
								<StatCardSkeleton />
								<StatCardSkeleton />
							</>
						) : stats.activeListings > 0 ? (
							<>
								<StatCard
									value={String(stats.activeListings)}
									label={t("stats.activeListings_label")}
									sub={t("stats.activeListings_sub")}
								/>
								<StatCard
									value={String(stats.sellers)}
									label={t("stats.sellers_label")}
									sub={t("stats.sellers_sub")}
								/>
								<StatCard
									value={t("stats.free_value")}
									label={t("stats.free_label")}
									sub={t("stats.free_sub")}
									accent
								/>
							</>
						) : null}
					</div>
				</div>
			</section>

			{/* ── Browse by type ── */}
			<section className="border-t border-border bg-background py-20">
				<div className="page-wrap">
					<p className="sec-tag">{t("discTypes.tag")}</p>
					<h2 className="display-title mb-10 text-[clamp(36px,4vw,52px)] leading-none tracking-[0.01em] text-foreground">
						{t("discTypes.heading")}
					</h2>
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
						{DISC_TYPES.map(({ labelKey, discClass, color, gradId }) => (
							<Link
								key={discClass}
								to="/listings"
								search={{ ...BROWSE_SEARCH, discClass: [discClass] }}
								className="group flex flex-col items-center gap-4 rounded-[var(--radius)] border border-border bg-card px-5 pb-5 pt-7 text-center transition-all duration-200 hover:-translate-y-0.5 hover:border-primary hover:shadow-lg"
							>
								<div
									className="flex h-[100px] w-[100px] items-center justify-center rounded-full"
									style={{
										background: `radial-gradient(circle at 40% 35%, color-mix(in srgb, ${color} 20%, transparent), transparent 65%), color-mix(in srgb, ${color} 8%, var(--muted))`,
									}}
								>
									<TypeDiscSvg color={color} gradId={gradId} />
								</div>
								<div>
									<div className="display-title text-[18px] tracking-[0.04em] text-foreground">
										{t(labelKey)}
									</div>
								</div>
								<div className="mt-auto text-[13px] text-muted-foreground transition-colors group-hover:text-primary">
									{t("discTypes.cta")}
								</div>
							</Link>
						))}
					</div>
				</div>
			</section>

			{/* ── How it works ── */}
			<section
				id="how-it-works"
				className="border-t border-border bg-muted py-20"
			>
				<div className="page-wrap">
					<p className="sec-tag">{t("howItWorks.tag")}</p>
					<h2 className="display-title mb-12 text-[clamp(36px,4vw,52px)] leading-none tracking-[0.01em] text-foreground">
						{t("howItWorks.heading")}
					</h2>
					<div className="grid gap-6 sm:grid-cols-3">
						{steps.map(({ num, n, title, body }) => (
							<div
								key={num}
								className="group relative overflow-hidden rounded-[var(--radius)] border border-border bg-card p-8"
							>
								<span
									aria-hidden="true"
									className="absolute right-5 top-5 text-[9px] font-bold uppercase tracking-[0.1em]"
									style={{ color: "var(--primary-accessible)" }}
								>
									{n}
								</span>
								<div
									aria-hidden="true"
									className="display-title mb-0.5 text-[100px] leading-[0.85] text-foreground/50 transition-colors duration-200 group-hover:text-foreground/70"
								>
									{num}
								</div>
								<h3 className="mb-2.5 text-sm font-bold uppercase tracking-[0.06em] text-foreground">
									{title}
								</h3>
								<p className="text-sm leading-[1.75] text-muted-foreground">
									{body}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ── Featured listings ── */}
			<section className="border-t border-border bg-background py-20">
				<div className="page-wrap">
					<div className="mb-9 flex items-end justify-between">
						<div>
							<p className="sec-tag">{t("featured.tag")}</p>
							<h2 className="display-title text-[clamp(36px,4vw,52px)] leading-none tracking-[0.01em] text-foreground">
								{t("featured.heading")}
							</h2>
						</div>
						<Link
							to="/listings"
							search={BROWSE_SEARCH}
							className="whitespace-nowrap rounded-[var(--radius)] border border-border px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground transition-all hover:bg-foreground hover:text-background"
						>
							{t("featured.viewAll")}
						</Link>
					</div>

					{listingsPending ? (
						<FeaturedSkeleton />
					) : listings.length === 0 ? (
						<div className="rounded-[var(--radius)] border border-dashed border-border py-20 text-center text-muted-foreground">
							{t("featured.empty")}
						</div>
					) : (
						<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
							{listings.map((listing, i) => (
								<ListingCard
									key={listing._id}
									listing={listing}
									isSaved={savedIds.has(listing._id)}
									onSaveToggle={() => handleSaveToggle(listing._id)}
									eager={i < 3}
									priority={i === 0}
								/>
							))}
						</div>
					)}
				</div>
			</section>

			{/* ── Sell CTA ── */}
			<section className="relative overflow-hidden border-t border-border bg-foreground py-20 dark:bg-[#ddd0b4]">
				{/* Dot grid */}
				<div
					className="pointer-events-none absolute inset-0 opacity-30"
					style={{
						backgroundImage:
							"radial-gradient(circle, color-mix(in srgb, var(--primary) 20%, transparent) 1.5px, transparent 1.5px)",
						backgroundSize: "32px 32px",
					}}
				/>
				{/* Bottom-left glow */}
				<div className="pointer-events-none absolute -bottom-20 -left-20 h-[500px] w-[500px] rounded-full bg-primary opacity-[0.08]" />

				<div className="page-wrap relative z-10 grid grid-cols-1 items-center gap-16 lg:grid-cols-[1fr_auto]">
					<div>
						<p className="mb-4 flex items-center gap-2.5 text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
							<span className="inline-block h-0.5 w-6 bg-primary" />
							{t("sellCta.tag")}
						</p>
						<h2 className="display-title mb-5 text-[clamp(52px,7vw,88px)] leading-[0.88] text-background">
							{t("sellCta.heading_1")}
							<br />
							{t("sellCta.heading_2_prefix")}
							<span className="text-primary">
								{t("sellCta.heading_2_accent")}
							</span>
						</h2>
						<p
							className="mb-9 max-w-[420px] text-base leading-[1.7]"
							style={{
								color: "color-mix(in srgb, var(--background) 70%, transparent)",
							}}
						>
							{t("sellCta.body")}
						</p>
						<div className="flex flex-wrap gap-3">
							<Button size="lg" asChild>
								<Link to="/sell">{t("sellCta.cta_create")}</Link>
							</Button>
							<Button
								size="lg"
								variant="outline"
								className="border-background/30 bg-transparent text-background hover:border-background hover:bg-transparent hover:text-background"
								asChild
							>
								<Link to="/" hash="how-it-works">
									{t("sellCta.cta_how")}
								</Link>
							</Button>
						</div>
					</div>

					{/* Ring decoration — only shown when there are real listings */}
					{stats && stats.activeListings > 0 && (
						<div className="relative hidden h-[220px] w-[220px] flex-shrink-0 items-center justify-center lg:flex">
							<div
								className="absolute h-[200px] w-[200px] rounded-full border-[20px] border-primary opacity-[0.15]"
								style={{ animation: "disc-spin 60s linear infinite" }}
							>
								<div className="absolute inset-[30px] rounded-full border-[6px] border-primary" />
							</div>
							<div
								className="absolute h-[140px] w-[140px] rounded-full border-[14px] border-primary opacity-10"
								style={{ animation: "disc-spin 40s linear infinite reverse" }}
							/>
							<div className="relative z-10 text-center">
								<div className="display-title text-[64px] leading-none text-primary">
									{stats.activeListings}
								</div>
								<div
									className="text-[10px] font-bold uppercase tracking-[0.1em]"
									style={{
										color:
											"color-mix(in srgb, var(--background) 55%, transparent)",
									}}
								>
									{t("sellCta.activeListings_label")}
								</div>
							</div>
						</div>
					)}
				</div>
			</section>

			{/* ── Support banner ── */}
			<SupportBanner />
		</>
	);
}

function SkBar({ className = "" }: { className?: string }) {
	return <div className={`animate-pulse rounded-full bg-muted ${className}`} />;
}

function HomeSkeleton() {
	return (
		<>
			{/* Hero */}
			<section className="relative overflow-hidden bg-background">
				<div
					className="pointer-events-none absolute inset-0"
					style={{
						backgroundImage:
							"radial-gradient(circle, var(--border) 1.2px, transparent 1.2px)",
						backgroundSize: "30px 30px",
					}}
				/>
				<div className="page-wrap relative z-10 py-24 lg:py-28">
					<div className="flex max-w-xl flex-col gap-5">
						<SkBar className="h-3 w-20" />
						<div className="flex flex-col gap-4">
							<SkBar className="h-[clamp(56px,8vw,96px)] w-3/4 rounded-lg" />
							<SkBar className="h-[clamp(56px,8vw,96px)] w-2/3 rounded-lg" />
							<SkBar className="h-[clamp(56px,8vw,96px)] w-1/2 rounded-lg" />
						</div>
						<div className="flex flex-col gap-2">
							<SkBar className="h-3 w-full max-w-sm" />
							<SkBar className="h-3 w-4/5 max-w-xs" />
						</div>
						<div className="flex gap-3">
							<SkBar className="h-11 w-32 rounded-lg" />
							<SkBar className="h-11 w-28 rounded-lg" />
						</div>
					</div>
				</div>
			</section>

			{/* Browse by type */}
			<section className="border-t border-border bg-background py-20">
				<div className="page-wrap">
					<SkBar className="mb-3 h-3 w-20" />
					<SkBar className="mb-10 h-10 w-64 rounded-lg" />
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
						{[0, 1, 2, 3].map((i) => (
							<div
								key={i}
								className="flex flex-col items-center gap-4 rounded-[var(--radius)] border border-border bg-card px-5 pb-5 pt-7"
							>
								<SkBar className="h-[100px] w-[100px] rounded-full" />
								<SkBar className="h-5 w-24 rounded-full" />
								<SkBar className="h-3 w-16 rounded-full" />
							</div>
						))}
					</div>
				</div>
			</section>

			{/* How it works */}
			<section className="border-t border-border bg-muted py-20">
				<div className="page-wrap">
					<SkBar className="mb-3 h-3 w-20" />
					<SkBar className="mb-12 h-10 w-64 rounded-lg" />
					<div className="grid gap-6 sm:grid-cols-3">
						{[0, 1, 2].map((i) => (
							<div
								key={i}
								className="rounded-[var(--radius)] border border-border bg-card p-8"
							>
								<SkBar className="mb-4 h-20 w-16 rounded-lg opacity-10" />
								<SkBar className="mb-3 h-3 w-32" />
								<div className="flex flex-col gap-2">
									<SkBar className="h-3 w-full" />
									<SkBar className="h-3 w-4/5" />
									<SkBar className="h-3 w-3/4" />
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Featured listings */}
			<section className="border-t border-border bg-background py-20">
				<div className="page-wrap">
					<div className="mb-9">
						<SkBar className="mb-3 h-3 w-20" />
						<SkBar className="h-10 w-48 rounded-lg" />
					</div>
					<FeaturedSkeleton />
				</div>
			</section>

			{/* Sell CTA */}
			<section className="relative overflow-hidden border-t border-border bg-foreground py-20 dark:bg-[#ddd0b4]">
				<div className="page-wrap relative z-10">
					<div className="flex max-w-xl flex-col gap-5">
						<SkBar className="h-3 w-20 bg-muted/30" />
						<div className="flex flex-col gap-4">
							<SkBar className="h-20 w-3/4 rounded-lg bg-muted/20" />
							<SkBar className="h-20 w-1/2 rounded-lg bg-muted/20" />
						</div>
						<SkBar className="h-3 w-96 bg-muted/20" />
						<div className="flex gap-3">
							<SkBar className="h-11 w-36 rounded-lg bg-muted/30" />
							<SkBar className="h-11 w-32 rounded-lg bg-muted/20" />
						</div>
					</div>
				</div>
			</section>
		</>
	);
}

function FeaturedSkeleton() {
	return (
		<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
			{Array.from({ length: 3 }, (_, i) => `fs-${i}`).map((id) => (
				<div
					key={id}
					className="overflow-hidden rounded-[var(--radius)] border border-border bg-card"
				>
					<div className="h-[230px] animate-pulse bg-muted" />
					<div className="flex flex-col gap-3 px-4 pb-4 pt-3">
						<div className="flex items-end justify-between gap-2">
							<div className="flex flex-col gap-1.5">
								<div className="h-2 w-16 animate-pulse rounded-full bg-muted" />
								<div className="h-4 w-32 animate-pulse rounded-full bg-muted" />
							</div>
							<div className="h-4 w-10 animate-pulse rounded-full bg-muted" />
						</div>
						<div className="h-10 animate-pulse rounded-lg bg-muted" />
						<div className="h-3 w-24 animate-pulse rounded-full bg-muted" />
					</div>
				</div>
			))}
		</div>
	);
}
